import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { nombre: "", telefono: "", telefono2: "", email: "", direccion: "", ciudad: "", notas: "", descuento_pct: 0 };

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/customers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar cliente "${c.nombre}"?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((c) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (c.nombre || "").toLowerCase().includes(s)
        || (c.telefono || "").toLowerCase().includes(s)
        || (c.ciudad || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">CRM</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1">Clientes</h1>
          <p className="text-zinc-500 mt-2 text-sm">Directorio de clientes conectado a la hoja de pedidos.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Input placeholder="Buscar por nombre, teléfono o ciudad..." value={q} onChange={(e) => setQ(e.target.value)}
                 className="md:w-72" data-testid="customer-search" />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                      className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-customer-btn">
                <Plus className="w-4 h-4" /> Nuevo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
                <DialogDescription>Sus datos se usarán como referencia en pedidos.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nombre completo</Label>
                  <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="customer-nombre" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Teléfono principal</Label>
                    <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="customer-telefono" />
                  </div>
                  <div>
                    <Label>Teléfono alternativo</Label>
                    <Input value={form.telefono2} onChange={(e) => setForm({ ...form, telefono2: e.target.value })} data-testid="customer-telefono2" />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label>Dirección</Label>
                  <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
                </div>
                <div>
                  <Label>Ciudad / Departamento</Label>
                  <Input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} />
                </div>
                <div>
                  <Label>Descuento fijo del cliente (%)</Label>
                  <Input type="number" min={0} max={100} step="0.5" value={form.descuento_pct}
                         onChange={(e) => setForm({ ...form, descuento_pct: Number(e.target.value) })}
                         data-testid="customer-descuento" />
                  <p className="text-[11px] text-zinc-500 mt-1">Se aplicará automáticamente al crear pedidos.</p>
                </div>
                <div>
                  <Label>Notas</Label>
                  <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-customer-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Nombre</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Teléfono</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Ciudad</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Descuento</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Dirección</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center text-zinc-400 py-8">Sin clientes.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50">
                  <td className="px-5 py-3 font-semibold">
                    {c.nombre}
                    <div className="text-[10px] font-normal text-zinc-400" data-testid={`cliente-creado-por-${c.id}`}>Creado por: {c.creado_por_nombre || c.creado_por || "—"}</div>
                  </td>
                  <td className="px-5 py-3 text-zinc-600">
                  {c.telefono || "—"}
                  {c.telefono2 && <div className="text-[11px] text-zinc-400">{c.telefono2}</div>}
                </td>
                  <td className="px-5 py-3 text-zinc-600">{c.ciudad || "—"}</td>
                  <td className="px-5 py-3">
                    {c.descuento_pct ? (
                      <span className="text-[11px] font-semibold uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                        -{c.descuento_pct}%
                      </span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-zinc-600 max-w-xs truncate">{c.direccion || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
